import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Mail, BellRing } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PublicPage, PageHero, Seo } from "@/components/public/PublicChrome";

const DOT = { operational: "text-emerald-400", degraded: "text-amber-400", outage: "text-red-400" };
const ICON = { operational: CheckCircle2, degraded: AlertTriangle, outage: XCircle };
const OVERALL = {
  operational: "All systems operational",
  degraded: "Some systems degraded",
  outage: "We're experiencing an outage",
};
const KIND_STYLE = {
  incident: "border-red-500/30 text-red-300", degradation: "border-amber-500/30 text-amber-300",
  maintenance: "border-blue-500/30 text-blue-300", resolved: "border-emerald-500/30 text-emerald-300",
};

function SubscribeCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async () => {
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/status/subscribe", { email });
      setDone(true);
      toast.success("You're subscribed to status updates!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not subscribe"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-r from-[#151310] to-[#121214] p-5 mb-8" data-testid="status-subscribe">
      <div className="flex items-center gap-2 mb-1 text-white font-medium"><BellRing className="w-4 h-4 text-[#D4AF37]" /> Get status updates</div>
      <p className="text-xs text-[#A1A1AA] mb-3">Be the first to know about maintenance, incidents, and when services are restored.</p>
      {done ? (
        <p className="text-sm text-emerald-300" data-testid="status-subscribe-done">✓ Subscribed. Check your inbox for confirmation.</p>
      ) : (
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 bg-[#0d0d0f] border border-white/10 rounded-full px-4">
            <Mail className="w-4 h-4 text-[#71717A]" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="status-subscribe-email"
              type="email" placeholder="you@company.com"
              className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none" />
          </div>
          <button onClick={subscribe} disabled={busy} data-testid="status-subscribe-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 hover:opacity-90 disabled:opacity-50">
            {busy ? "…" : "Subscribe"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Status() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/status").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <PublicPage>
      <Seo title="System Status" description="Real-time status of Fairview Systems services." path="/status" />
      <PageHero eyebrow="Trust Center" title="System Status" subtitle="Live status of every Fairview Systems service." />
      <div className="max-w-3xl mx-auto px-6 pb-24">
        {data && (
          <>
            <div className={`flex items-center gap-3 rounded-2xl border p-5 mb-6 ${data.overall === "operational" ? "border-emerald-500/30 bg-emerald-500/5" : data.overall === "degraded" ? "border-amber-500/30 bg-amber-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid="status-overall">
              <CheckCircle2 className={`w-6 h-6 ${DOT[data.overall]}`} />
              <span className="text-white font-medium">{OVERALL[data.overall]}</span>
            </div>
            <div className="rounded-2xl border border-white/8 bg-[#121214] divide-y divide-white/5 mb-8">
              {data.services.map((s) => {
                const I = ICON[s.status] || CheckCircle2;
                return (
                  <div key={s.service} className="flex items-center justify-between px-5 py-3.5" data-testid={`status-${s.service.toLowerCase().replace(/\s/g, "-")}`}>
                    <span className="text-[#D4D4D8] text-sm">{s.service}{s.note ? <span className="text-[#52525B] text-xs ml-2">{s.note}</span> : null}</span>
                    <span className={`flex items-center gap-1.5 text-xs capitalize ${DOT[s.status]}`}><I className="w-4 h-4" /> {s.status}</span>
                  </div>
                );
              })}
            </div>

            <SubscribeCard />

            <h3 className="text-white font-medium mb-3">Incident history</h3>
            {data.incidents.length === 0 ? (
              <p className="text-sm text-[#52525B]">No incidents reported. 🎉</p>
            ) : data.incidents.map((i, idx) => (
              <div key={idx} className="rounded-xl border border-white/8 bg-[#121214] p-4 mb-2" data-testid="status-incident">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium">{i.title}</span>
                  {i.kind && <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${KIND_STYLE[i.resolved ? "resolved" : i.kind] || "border-white/10 text-[#A1A1AA]"}`}>{i.resolved ? "resolved" : (i.status || i.kind)}</span>}
                </div>
                {i.message && <div className="text-xs text-[#A1A1AA] mt-1">{i.message}</div>}
                <div className="text-xs text-[#52525B] mt-1">{i.created_at}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </PublicPage>
  );
}
